"use client";

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { OTPVerify, OTPVerifySchema } from "@/lib/validations";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { ArrowLeft } from "lucide-react";
import { OTPVerificationFormProps } from "@/components/auth/types";
import { toast } from "@/hooks/use-toast";

export function OTPVerificationForm({
  onSubmit,
  status,
  onViewChange,
  email,
  password,
  onResendOTP,
  resendOTPStatus,
}: OTPVerificationFormProps) {
  const [countdown, setCountdown] = useState(30);
  const [attempts, setAttempts] = useState(0);
  const MAX_ATTEMPTS = 3;

  const form = useForm<OTPVerify>({
    resolver: zodResolver(OTPVerifySchema),
    defaultValues: {
      pin: "",
      email,
      password,
    },
  });

  useEffect(() => {
    if (countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [countdown]);

  const handleResend = async () => {
    if (countdown > 0 || attempts >= MAX_ATTEMPTS) {
      return;
    }

    if (!onResendOTP) {
      return;
    }

    try {
      const result = await onResendOTP({ email });
      if (result.success) {
        toast({
          title: "Code Resent",
          description: "A new verification code has been sent to your email.",
          variant: "success",
        });
        setAttempts((prev) => prev + 1);
        setCountdown(30);
      } else if (result.error) {
        toast({
          title: "Error",
          description: result.error,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
      toast({
        title: "Error",
        description: "Failed to resend verification code. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleOTPComplete = (value: string) => {
    if (value.length === 6) {
      form.setValue("pin", value);
      form.handleSubmit(handleSubmit)();
    }
  };

  const handleSubmit = async (values: OTPVerify) => {
    try {
      const result = await onSubmit(values);

      if (result?.error) {
        toast({
          title: "Error",
          description: result.error,
          variant: "destructive",
        });
        form.setError("pin", { message: result.error });
        // Clear the OTP input on error
        form.setValue("pin", "");
        return;
      }

      // Let the auth manager handle the sign in process
      if (result?.success) {
        toast({
          title: "Success",
          description: "Email verified successfully. Signing you in...",
          variant: "success",
        });
        return result;
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      toast({
        title: "Error",
        description: "An error occurred. Please try again.",
        variant: "destructive",
      });
      form.setError("pin", {
        message: "An error occurred. Please try again.",
      });
    }
  };

  return (
    <div className='space-y-4'>
      <h2 className='text-2xl font-bold'>Verify Your Email</h2>
      <p className='text-sm text-muted-foreground'>
        We sent a verification code to {email}
      </p>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-4'>
          <FormField
            control={form.control}
            name='pin'
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <InputOTP
                    maxLength={6}
                    value={field.value}
                    onChange={field.onChange}
                    onComplete={handleOTPComplete}
                    disabled={status === "executing"}
                  >
                    <InputOTPGroup className='w-full'>
                      <InputOTPSlot
                        index={0}
                        className='flex-1 h-16 text-4xl'
                      />
                      <InputOTPSlot
                        index={1}
                        className='flex-1 h-16 text-4xl'
                      />
                      <InputOTPSlot
                        index={2}
                        className='flex-1 h-16 text-4xl'
                      />
                      <InputOTPSlot
                        index={3}
                        className='flex-1 h-16 text-4xl'
                      />
                      <InputOTPSlot
                        index={4}
                        className='flex-1 h-16 text-4xl'
                      />
                      <InputOTPSlot
                        index={5}
                        className='flex-1 h-16 text-4xl'
                      />
                    </InputOTPGroup>
                  </InputOTP>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type='submit'
            className='w-full'
            disabled={status === "executing"}
          >
            {status === "executing" ? "Verifying..." : "Verify Email"}
          </Button>
        </form>
      </Form>

      <div className='flex flex-col items-center space-y-2'>
        {attempts < MAX_ATTEMPTS ? (
          <>
            {countdown > 0 ? (
              <p className='text-sm text-muted-foreground'>
                Resend code in {countdown}s
              </p>
            ) : (
              <Button
                variant='link'
                onClick={(e) => {
                  e.preventDefault();
                  handleResend();
                }}
                disabled={resendOTPStatus === "executing" || countdown > 0}
                className='h-auto p-0 font-normal'
                type='button'
              >
                Didn&apos;t receive the code? Click to resend
              </Button>
            )}
            {resendOTPStatus === "executing" && (
              <p className='text-sm text-muted-foreground'>Sending...</p>
            )}
          </>
        ) : (
          <p className='text-sm text-destructive'>
            Maximum resend attempts reached. Please try again later.
          </p>
        )}
      </div>

      <div>
        <Button
          variant='link'
          className='h-auto p-0 font-normal'
          onClick={() => onViewChange?.("register")}
          type='button'
        >
          <ArrowLeft className='mr-2 h-4 w-4' />
          Back
        </Button>
      </div>
    </div>
  );
}
